import { NavLink } from 'react-router-dom';
import { resolveAssetUrl } from '../../utils/resolveAssetUrl';
import BrandLogo from '../BrandLogo';

const navItems = [
  { label: 'Dashboard', path: '/host/dashboard', icon: 'bi-speedometer2' },
  { label: 'My Properties', path: '/host/properties', icon: 'bi-house-door', badge: 'propertiesCount' },
  { label: 'Calendar & Pricing', path: '/host/calendar', icon: 'bi-calendar3', badge: 'upcomingArrivals' },
  { label: 'Bookings', path: '/host/bookings', icon: 'bi-journal-text', badge: 'totalBookings' },
  { label: 'Cancellations & Refunds', path: '/host/cancellations', icon: 'bi-x-circle' },
  { label: 'Payouts', path: '/host/payouts', icon: 'bi-cash-stack', badge: 'pendingPayments' },
  { label: 'Statement', path: '/host/transactions', icon: 'bi-receipt' },
  { label: 'Invoices', path: '/host/invoices', icon: 'bi-file-earmark-text' },
  { label: 'Bank Account', path: '/host/bank-account', icon: 'bi-bank' },
  { label: 'Messages', path: '/host/messages', icon: 'bi-chat-dots', badge: 'unreadMessages' },
  { label: 'Reviews', path: '/host/reviews', icon: 'bi-star' },
  { label: 'Pricing & Deals', path: '/host/pricing', icon: 'bi-tags' },
  { label: 'Promotions', path: '/host/promotions', icon: 'bi-megaphone' },
  { label: 'Reports', path: '/host/reports', icon: 'bi-graph-up' },
  { label: 'Support', path: '/host/support', icon: 'bi-life-preserver', badge: 'openTickets' },
  { label: 'Profile & KYC', path: '/host/profile', icon: 'bi-person-circle' },
];

const resolveAvatar = (host) => {
  const avatarUrl = host?.profile_image_url || host?.photo_url || host?.profile_photo_url;
  if (!avatarUrl) return '';
  return resolveAssetUrl(avatarUrl);
};

const Badge = ({ value, collapsed }) => {
  if (!value || Number(value) <= 0) return null;
  if (collapsed) {
    return <span className="ml-auto h-2 w-2 rounded-full bg-emerald-400" />;
  }
  return (
    <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700">
      {value}
    </span>
  );
};

const HostSidebar = ({
  host,
  summary,
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onCloseMobile,
  onLogout,
}) => {
  const avatar = resolveAvatar(host);
  const initials = host?.full_name?.slice(0, 2)?.toUpperCase() || 'HV';
  const shellWidth = collapsed ? 'w-20' : 'w-72';
  const translate = mobileOpen ? 'translate-x-0' : '-translate-x-full';

  return (
    <>
      <div
        className={`fixed inset-0 z-40 bg-slate-950/60 transition-opacity lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={onCloseMobile}
        role="button"
        tabIndex={-1}
        aria-label="Close sidebar"
      />
      <aside
        className={`fixed left-0 top-0 z-50 h-full ${shellWidth} ${translate} transition-all duration-200 lg:translate-x-0`}
      >
        <div className="flex h-full min-h-0 flex-col bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-slate-100 shadow-xl">
          <div className="flex items-center justify-between px-4 py-5">
            <div className={`flex items-center gap-3 ${collapsed ? 'justify-center w-full' : ''}`}>
              <BrandLogo
                size={40}
                className="rounded-lg bg-white"
                imgClassName="p-1"
                fallback="HV"
                fallbackClassName="text-slate-900 font-semibold"
              />
              {!collapsed && (
                <div>
                  <p className="text-xs uppercase tracking-widest text-emerald-200">Homevilas</p>
                  <p className="text-sm font-semibold text-white">Host Suite</p>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={onToggleCollapse}
              className="hidden h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-sm text-white hover:bg-white/20 lg:flex"
              title={collapsed ? 'Expand' : 'Collapse'}
            >
              <i className={`bi ${collapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`} />
            </button>
          </div>

          <div className={`px-4 ${collapsed ? 'hidden' : ''}`}>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <div className="flex items-center gap-3">
                {avatar ? (
                  <img src={avatar} alt={host?.full_name || 'Host'} className="h-10 w-10 rounded-full object-cover" />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-500 text-white font-semibold">
                    {initials}
                  </div>
                )}
                <div>
                  <p className="text-sm font-semibold text-white">{host?.full_name || 'Host'}</p>
                  <p className="text-xs text-slate-300">{host?.email || host?.phone || 'Host access'}</p>
                </div>
              </div>
              <span className="mt-2 inline-flex rounded-full bg-emerald-500/20 px-2 py-0.5 text-xs text-emerald-200">
                Host
              </span>
            </div>
          </div>

          <nav className="mt-6 flex-1 space-y-1 overflow-y-auto px-3">
            {navItems.map((item) => (
              <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? item.label : undefined}
                className={({ isActive }) =>
                  `group relative flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium transition ${
                    isActive
                      ? 'bg-emerald-500/15 text-white ring-1 ring-emerald-400/30'
                      : 'text-slate-200 hover:bg-white/10'
                  }`
                }
              >
                <i className={`bi ${item.icon} text-base`} />
                {!collapsed && <span>{item.label}</span>}
                <Badge value={summary?.[item.badge]} collapsed={collapsed} />
                {collapsed && (
                  <span className="pointer-events-none absolute left-full ml-3 rounded-lg bg-slate-900 px-2 py-1 text-xs text-white opacity-0 shadow-lg transition group-hover:opacity-100">
                    {item.label}
                  </span>
                )}
              </NavLink>
            ))}
          </nav>

          <div className="px-3 pb-6">
            <button
              type="button"
              onClick={onLogout}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-slate-200 hover:bg-white/10"
              title={collapsed ? 'Logout' : undefined}
            >
              <i className="bi bi-box-arrow-right text-base" />
              {!collapsed && <span>Logout</span>}
            </button>
          </div>
        </div>
      </aside>
    </>
  );
};

export default HostSidebar;
